<template>
  <UPageSection
    :dir="dir"
    :title="title"
    :description="resolvedSubtitle"
    :ui="{
      root: 'min-w-0 overflow-x-clip',
      container: 'w-full max-w-full min-w-0 !gap-0 py-10 sm:py-14 lg:grid-cols-1 lg:py-10',
      title: 'max-w-full text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl text-center',
      description: 'mx-auto mt-4 max-w-3xl text-lg text-gray-500 dark:text-gray-400 text-center',
    }"
  >
    <div class="min-w-0 w-full">
      <div
        v-if="loading && !builderPreview"
        class="mx-auto my-8 grid w-full items-stretch gap-5 sm:my-12 sm:grid-cols-2 xl:grid-cols-3"
        :class="cards.length === 1 ? 'max-w-3xl grid-cols-1' : ''"
        aria-label="در حال بارگذاری خدمات"
        aria-busy="true"
      >
        <div
          v-for="index in 3"
          :key="index"
          class="animate-pulse overflow-hidden rounded-2xl border border-default bg-default p-5 shadow-sm"
        >
          <div class="mx-auto size-14 rounded-2xl bg-elevated" />
          <div class="mx-auto mt-5 h-5 w-3/4 rounded bg-elevated" />
          <div class="mx-auto mt-3 h-4 w-1/2 rounded bg-elevated" />
          <div class="mt-6 h-11 rounded-xl bg-elevated" />
          <div class="mx-auto mt-4 h-4 w-2/5 rounded bg-elevated" />
        </div>
      </div>

      <div
        v-else-if="isGrid && cards.length"
        class="mx-auto grid w-full items-stretch gap-3 sm:gap-5"
        :class="cards.length === 1 ? 'max-w-3xl grid-cols-1' : 'grid-cols-2 xl:grid-cols-3'"
      >
        <ServicePricingCard
          v-for="card in cards"
          :key="card.key"
          v-bind="card.props"
          :builder-preview="builderPreview"
          :preview-navigate="previewNavigate || onNavigate"
          :on-navigate="onNavigate"
          :cta-handler="card.ctaHandler"
          @cta="card.ctaHandler?.()"
        />
      </div>

      <ClientOnly v-else-if="cards.length">
        <div
          class="block-appointment__swiper mx-auto my-8 min-w-0 w-full sm:my-12"
          :class="cards.length === 1 ? 'max-w-3xl' : ''"
        >
          <Swiper
            :modules="swiperModules"
            :dir="dir"
            :slides-per-view="slidesPerView"
            :space-between="24"
            :breakpoints-base="'container'"
            :breakpoints="swiperBreakpoints"
            :pagination="{ clickable: true, dynamicBullets: true }"
            :autoplay="autoplayOptions"
            :loop="canLoop"
            class="equal-h-swiper overflow-hidden"
          >
            <SwiperSlide
              v-for="card in cards"
              :key="card.key"
              class="h-auto py-2"
            >
              <ServicePricingCard
                class="h-full"
                v-bind="card.props"
                :builder-preview="builderPreview"
                :preview-navigate="previewNavigate || onNavigate"
                :on-navigate="onNavigate"
                :cta-handler="card.ctaHandler"
                @cta="card.ctaHandler?.()"
              />
            </SwiperSlide>
          </Swiper>
        </div>
        <template #fallback>
          <div
            class="mx-auto grid w-full items-stretch gap-3 sm:gap-5"
            :class="cards.length === 1 ? 'max-w-3xl grid-cols-1' : 'grid-cols-2 xl:grid-cols-3'"
          >
            <ServicePricingCard
              v-for="card in cards"
              :key="card.key"
              v-bind="card.props"
            />
          </div>
        </template>
      </ClientOnly>

      <p
        v-else-if="!loading"
        class="text-center text-sm text-muted"
      >
        {{ emptyLabel }}
      </p>
    </div>

  </UPageSection>
</template>

<script setup lang="ts">
import { Autoplay, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import ServicePricingCard from '~/components/blocks/ServicePricingCard.vue'
import type { ServiceCardVariant } from '~/constants/serviceCardVariants'
import type { BookingServicePublic } from '~/types/booking'

const dir = useDir()
const pageLocale = useState<string>('page-locale', () => 'fa')

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    title?: string
    subtitle?: string
    /** @deprecated legacy alias for subtitle in saved schemas */
    description?: string
    layout?: 'carousel' | 'grid'
    cardVariant?: ServiceCardVariant
    autoplay?: boolean
    autoplayDelay?: number
    loop?: boolean
    builderPreview?: boolean
    previewDevice?: 'desktop' | 'mobile'
    previewNavigate?: (to: string) => void
    onNavigate?: (to: string) => void
  }>(),
  {
    layout: 'grid',
    cardVariant: 'soft',
    autoplay: true,
    autoplayDelay: 5000,
    loop: true,
  },
)

const resolvedSubtitle = computed(() => props.subtitle || props.description)

const isGrid = computed(() => props.layout !== 'carousel')
const swiperModules = [Pagination, Autoplay]
const slidesPerView = computed(() => Math.min(2, Math.max(1, cards.value.length)))

const swiperBreakpoints = computed(() => {
  const max = Math.max(1, cards.value.length)
  return {
    0: { slidesPerView: Math.min(2, max), spaceBetween: 10 },
    360: { slidesPerView: Math.min(2.2, max), spaceBetween: 12 },
    480: { slidesPerView: Math.min(2.5, max), spaceBetween: 14 },
    600: { slidesPerView: Math.min(2.8, max), spaceBetween: 16 },
    768: { slidesPerView: Math.min(3, max), spaceBetween: 20 },
    1200: { slidesPerView: Math.min(3, max), spaceBetween: 24 },
  }
})

const emptyLabel = computed(() =>
  pageLocale.value === 'fa' ? 'هنوز خدمت قابل رزروی تعریف نشده است.' : 'No bookable services yet.',
)
const defaultCta = computed(() =>
  pageLocale.value === 'fa' ? 'دریافت نوبت' : 'Book now',
)

const autoplayDelay = computed(() => {
  const value = Number(props.autoplayDelay)
  if (!Number.isFinite(value) || value < 1000) return 5000
  return Math.min(60000, Math.round(value))
})

const canLoop = computed(() => Boolean(props.loop) && cards.value.length > 1)

const autoplayOptions = computed(() => {
  if (!props.autoplay || cards.value.length < 2) return false
  return {
    delay: autoplayDelay.value,
    disableOnInteraction: true,
    pauseOnMouseEnter: true,
    stopOnLastSlide: !props.loop,
  }
})

const { requestBooking } = useBookingRequest()
const services = ref<BookingServicePublic[]>([])
const loading = ref(true)

const previewCards: BookingServicePublic[] = [{
  id: 'preview',
  serviceRef: 'preview',
  title: pageLocale.value === 'fa' ? 'ویزیت عمومی' : 'General visit',
  durationMin: 15,
  visitTypes: 'both',
  paymentMode: 'free',
  price: 0,
  depositAmount: null,
  allowOnsitePayment: false,
  readinessQuizId: null,
  content: {
    description: pageLocale.value === 'fa'
      ? 'معاینه و مشاوره متناسب با نیاز شما.'
      : 'A visit tailored to your needs.',
    price: pageLocale.value === 'fa' ? 'رایگان' : 'Free',
    icon: 'i-lucide-stethoscope',
    image: '',
    features: pageLocale.value === 'fa'
      ? ['مشاوره اولیه', 'مرور داروهای جاری']
      : ['Initial consult', 'Medication review'],
    ctaLabel: defaultCta.value,
  },
}]

onMounted(async () => {
  loading.value = true
  try {
    const data = await $fetch<{ items: BookingServicePublic[] }>('/api/booking/services')
    services.value = data.items
  }
  catch {
    services.value = []
  }
  finally {
    loading.value = false
  }
})

const liveServices = computed(() => {
  if (services.value.length) return services.value
  return props.builderPreview ? previewCards : []
})

function openBooking(service: BookingServicePublic) {
  requestBooking(service)
}

type CardRow = {
  key: string
  props: {
    title?: string
    description?: string
    price?: string
    image?: string
    icon?: string
    detailLink: string
    features: string[]
    variant: ServiceCardVariant
    ctaLabel: string
    ctaLink: string
  }
  ctaHandler?: () => void
}

const cards = computed<CardRow[]>(() =>
  liveServices.value.map((service) => ({
    key: service.serviceRef || service.id,
    props: {
      title: service.title,
      description: service.content?.description,
      price: service.content?.price || (service.paymentMode === 'free' ? (pageLocale.value === 'fa' ? 'رایگان' : 'Free') : ''),
      image: service.content?.image,
      icon: service.content?.icon,
      detailLink: `/services/${encodeURIComponent(service.serviceRef)}`,
      features: service.content?.features ?? [],
      variant: props.cardVariant || 'soft',
      ctaLabel: service.content?.ctaLabel || defaultCta.value,
      ctaLink: `/booking?service=${service.serviceRef}#booking-wizard`,
    },
    ctaHandler: () => openBooking(service),
  })),
)
</script>

<style scoped>
.block-appointment__swiper :deep(.swiper-pagination) {
  position: relative !important;
  bottom: auto !important;
  margin-top: 1rem;
  width: 100%;
}

.block-appointment__swiper :deep(.swiper-pagination-bullet-active) {
  background-color: var(--ui-primary);
}
</style>
